package com.oasisfeng.invaluable.p2p.websocket

/**
 * Local retry state for one outbound WebSocket endpoint.
 *
 * Failures use exponential full-jitter backoff. After the configured consecutive-failure threshold, the circuit stays
 * open for a cooldown plus jitter before admitting one half-open retry. Call [onConnected] only after the transport has
 * remained healthy long enough to count as a real connection; a socket that merely opens and immediately flaps must
 * retain its failure history. This is derived process-local transport state, never replicated protocol state.
 */
class ReconnectPolicy internal constructor(
    private val initialBackoffMs: Int = 2_000,
    private val maxBackoffMs: Int = 60_000,
    private val circuitFailureThreshold: Int = 6,
    private val circuitOpenMs: Int = 300_000,
    private val random: () -> Double = { js("Math.random()") as Double },
) {
    constructor() : this(2_000, 60_000, 6, 300_000, { js("Math.random()") as Double })

    init {
        require(initialBackoffMs > 0)
        require(maxBackoffMs >= initialBackoffMs)
        require(circuitFailureThreshold > 1)
        require(circuitOpenMs > 0)
        require(circuitOpenMs.toLong() + maxBackoffMs <= Int.MAX_VALUE)
    }

    var consecutiveFailures: Int = 0
        private set

    var isCircuitOpen: Boolean = false
        private set

    /** Record a failed or short-lived connection and return the delay before the next attempt. */
    fun onFailure(): Int {
        consecutiveFailures++
        if (consecutiveFailures >= circuitFailureThreshold) {
            isCircuitOpen = true
            return circuitOpenMs + fullJitter(maxBackoffMs)
        }
        isCircuitOpen = false
        val exponent = (consecutiveFailures - 1).coerceAtMost(30)
        val ceiling = (initialBackoffMs.toLong() shl exponent).coerceAtMost(maxBackoffMs.toLong()).toInt()
        return fullJitter(ceiling)
    }

    /** Reset the breaker after a connection has proved stable. */
    fun onConnected() {
        consecutiveFailures = 0
        isCircuitOpen = false
    }

    private fun fullJitter(ceilingMs: Int): Int {
        val sample = random()
        require(sample >= 0.0 && sample < 1.0) { "random must return a value in [0, 1)" }
        return (sample * ceilingMs).toInt().coerceAtLeast(1)
    }
}
